// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import { ReportDialog } from '@/components/ReportDialog';
import { WorkspaceBillVersion, WorkspaceBill } from '@/types/workspace-bill';
import { useGetAIReview } from '@/api/workspace-bills-api';
import { useToast } from "@/hooks/use-toast";
import { formatSessionCode } from '@/utils/date';
import { useConfig } from '@/hooks/config/useConfig';
import type { AIAnalysis } from '@/types/workspace-bill';
import type { JSONSchema } from '@/api/types/schema';
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Loader2 } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface AILegislativeReviewProps {
  latestVersion: WorkspaceBillVersion;
  bill: WorkspaceBill;
  loadingAction?: string;
}

export function AILegislativeReview({ latestVersion, bill, loadingAction }: AILegislativeReviewProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [reportData, setReportData] = useState<AIAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const getAIReview = useGetAIReview();
  const { toast } = useToast();
  const { config } = useConfig();

  // Reset state when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      setHasAttemptedFetch(false);
      setReportData(null);
    }
  }, [dialogOpen]);

  // Fetch AI review data when dialog opens
  useEffect(() => {
    const fetchReviewData = async () => {
      if (!dialogOpen || !latestVersion || !bill.session || !bill.bill_number || hasAttemptedFetch) return;

      try {
        setIsLoading(true);
        setHasAttemptedFetch(true);

        // Format the session code to match API expectations
        const formatted_session = formatSessionCode(bill.session);

        const data = await getAIReview.mutateAsync({
          session: formatted_session,
          bill_number: bill.bill_number
        });


        setReportData(data);
      } catch (error) {
        console.error('Error fetching AI review:', error);
        toast({
          title: "Error",
          description: "Failed to load the AI review. Please try again.",
          variant: "destructive",
        });
        setReportData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviewData();
  }, [dialogOpen, bill, toast, getAIReview, hasAttemptedFetch]);

  const handle_open_dialog = () => {
    setDialogOpen(true);
  };

  // Generate dialog title
  const get_dialog_title = () => {
    const report_title = legislative_review_schema?.title || "AI Legislative Review";
    return `${report_title}: ${bill.bill_number}`;
  };

  // Get the legislative_review schema from config
  const legislative_review_schema = config?.prompt_config?.legislative_review?.json_schema as JSONSchema | undefined;

  // Check if a review is being generated
  const is_generating_review = loadingAction === 'LegislativeReview';

  // Check if a review exists - look at bill level, not version level
  const has_review = !!bill?.legislative_review_s3_key;

  // If no review exists and none is being generated, don't render anything
  if (!has_review && !is_generating_review) {
    return null;
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className={cn(
                "cursor-pointer transition-colors",
                has_review
                  ? "bg-blue-100 hover:bg-blue-200 text-blue-800"
                  : "bg-gray-100 text-gray-500"
              )}
              onClick={has_review ? handle_open_dialog : undefined}
            >
              {is_generating_review ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Generating
                </>
              ) : (
                <>
                  AI Review
                </>
              )}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {is_generating_review
              ? "AI review is being generated..."
              : has_review
                ? "Click to view AI legislative review"
                : "No AI review available"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <ReportDialog
        title={get_dialog_title()}
        report_data={reportData}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        isLoading={isLoading}
        schema={legislative_review_schema}
      />
    </>
  );
}
